import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { kaylasApi } from "./kaylasApi";
import { KaylasDrawer, FormSection } from "./KaylasDrawer";
import { Plus, Edit3, Trash2, Loader2, Map, Copy, Grid as GridIcon, Search, X, Eye } from "lucide-react";
import { toast } from "sonner";

/* Canvas scale: 1 cm = 0.6 px on screen (so a 10m garden = 600px) */
const PX_PER_CM = 0.6;
const cm2px = (cm) => cm * PX_PER_CM;

export default function KaylasBuilder() {
  const [gardens, setGardens] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMature, setShowMature] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // { bedId, x_cm, y_cm }
  const [editGarden, setEditGarden] = useState(null);
  const [editBed, setEditBed] = useState(null);
  const [summary, setSummary] = useState(null);

  const refresh = useCallback(async () => {
    const list = await kaylasApi.listGardens();
    setGardens(list);
    if (list.length && !list.find((g) => g.id === activeId)) {
      setActiveId(list[0].id);
    }
  }, [activeId]);

  useEffect(() => {
    let off = false;
    (async () => {
      try { const r = await kaylasApi.listGardens(); if (!off) { setGardens(r); setActiveId(r[0]?.id || null); } }
      finally { if (!off) setLoading(false); }
    })();
    return () => { off = true; };
  }, []);

  const active = gardens.find((g) => g.id === activeId);

  useEffect(() => {
    if (!activeId) { setSummary(null); return; }
    let off = false;
    const load = async () => {
      try {
        const s = await kaylasApi.gardenSummary(activeId);
        if (!off) setSummary(s);
      } catch { /* ignore */ }
    };
    load();
    return () => { off = true; };
  }, [activeId, gardens]);

  const handleAddGarden = async () => {
    const name = window.prompt("Name your new garden:");
    if (!name) return;
    const g = await kaylasApi.createGarden({ name });
    setGardens((prev) => [...prev, g]);
    setActiveId(g.id);
    toast.success(`${name} created`);
  };

  const handleDuplicate = async () => {
    if (!active) return;
    const g = await kaylasApi.duplicateGarden(active.id);
    setGardens((prev) => [...prev, g]);
    setActiveId(g.id);
    toast.success("Garden duplicated");
  };

  const handleDeleteGarden = async () => {
    if (!active) return;
    if (!window.confirm(`Delete "${active.name}" and all its beds & plants?`)) return;
    await kaylasApi.deleteGarden(active.id);
    toast.success("Garden removed");
    const next = gardens.filter((g) => g.id !== active.id);
    setGardens(next);
    setActiveId(next[0]?.id || null);
  };

  const handleAddBed = async () => {
    if (!active) return;
    const bed = await kaylasApi.addBed(active.id, { length_cm: 200, width_cm: 100, x: 30, y: 30 });
    setGardens((prev) => prev.map((g) => g.id === active.id ? { ...g, beds: [...(g.beds || []), bed] } : g));
  };

  const handleBedUpdate = async (bedId, fields) => {
    if (!active) return;
    await kaylasApi.updateBed(active.id, bedId, fields);
    setGardens((prev) => prev.map((g) => g.id === active.id
      ? { ...g, beds: g.beds.map((b) => b.id === bedId ? { ...b, ...fields } : b) }
      : g));
  };

  const handleBedDelete = async (bedId) => {
    if (!active) return;
    if (!window.confirm("Remove this bed and all its plants?")) return;
    await kaylasApi.deleteBed(active.id, bedId);
    setGardens((prev) => prev.map((g) => g.id === active.id ? { ...g, beds: g.beds.filter((b) => b.id !== bedId) } : g));
    toast.success("Bed removed");
  };

  const handlePlantPlace = async (record) => {
    if (!pickerFor || !active) return;
    const payload = {
      source_kind: record.kind,
      source_id: record.id,
      name: record.name,
      photo: record.photo,
      colour: record.raw?.colour || null,
      x: pickerFor.x_cm,
      y: pickerFor.y_cm,
      spacing_cm: 40,
      mature_cm: 60,
    };
    const plant = await kaylasApi.placePlant(active.id, pickerFor.bedId, payload);
    setGardens((prev) => prev.map((g) => g.id === active.id
      ? { ...g, beds: g.beds.map((b) => b.id === pickerFor.bedId ? { ...b, plants: [...(b.plants || []), plant] } : b) }
      : g));
    setPickerFor(null);
    toast.success(`${record.name} placed`);
  };

  const handlePlantMove = async (bedId, plantId, x, y) => {
    await kaylasApi.updatePlant(active.id, bedId, plantId, { x, y });
  };

  const handlePlantUpdateLocal = (bedId, plantId, fields) => {
    setGardens((prev) => prev.map((g) => g.id === active.id
      ? { ...g, beds: g.beds.map((b) => b.id === bedId ? { ...b, plants: b.plants.map((p) => p.id === plantId ? { ...p, ...fields } : p) } : b) }
      : g));
  };

  const handlePlantDelete = async (bedId, plantId) => {
    await kaylasApi.deletePlant(active.id, bedId, plantId);
    setGardens((prev) => prev.map((g) => g.id === active.id
      ? { ...g, beds: g.beds.map((b) => b.id === bedId ? { ...b, plants: b.plants.filter((p) => p.id !== plantId) } : b) }
      : g));
  };

  if (loading) return <div className="text-center py-20 text-[var(--mum-cobalt)]"><Loader2 size={28} className="animate-spin mx-auto mb-2" />Drawing the garden…</div>;

  return (
    <div className="space-y-5" data-testid="mums-builder-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><Map size={12} /> Tab 9</div>
          <h1 className="font-display text-4xl mt-2">Garden Builder</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">Sketch the patch, place the dahlias, watch it grow.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="mum-btn-ghost" onClick={() => setShowMature((v) => !v)} data-testid="builder-toggle-mature">
            <Eye size={13} /> {showMature ? "Spacing only" : "Show mature size"}
          </button>
          <button className="mum-btn" onClick={handleAddGarden} data-testid="builder-add-garden"><Plus size={15} /> New garden</button>
        </div>
      </header>

      {/* Garden tabs */}
      <nav className="flex gap-2 flex-wrap" data-testid="builder-garden-tabs">
        {gardens.map((g) => (
          <button
            key={g.id}
            type="button"
            className={`mum-tab ${g.id === activeId ? "is-active" : ""}`}
            onClick={() => setActiveId(g.id)}
            data-testid={`builder-tab-${g.id}`}
          >
            {g.name}
          </button>
        ))}
      </nav>

      {active && (
        <>
          {/* Toolbar */}
          <div className="kaylas-card p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="builder-toolbar">
            <div className="flex items-center gap-2 flex-wrap">
              <strong className="font-display text-lg text-[var(--mum-sapphire)]">{active.name}</strong>
              <span className="text-xs text-[var(--mum-cobalt)]/70">{Math.round(active.width_cm / 100)}m × {Math.round(active.length_cm / 100)}m</span>
            </div>
            <div className="flex gap-1 flex-wrap text-xs">
              <button className="mum-btn-ghost" onClick={() => setEditGarden(active)} data-testid="builder-edit-garden"><Edit3 size={12} /> Rename / Resize</button>
              <button className="mum-btn-ghost" onClick={handleDuplicate} data-testid="builder-duplicate"><Copy size={12} /> Duplicate</button>
              <button className="mum-btn-ghost" onClick={handleAddBed} data-testid="builder-add-bed"><GridIcon size={12} /> Add bed</button>
              <button className="mum-btn-ghost" onClick={handleDeleteGarden} data-testid="builder-delete-garden"><Trash2 size={12} /> Delete</button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            {/* Canvas */}
            <div className="kaylas-card p-3 overflow-auto" data-testid="builder-canvas-wrap">
              <GardenCanvas
                garden={active}
                showMature={showMature}
                onBedUpdate={handleBedUpdate}
                onBedDelete={handleBedDelete}
                onBedEdit={(b) => setEditBed(b)}
                onAddPlantHere={(bedId, x_cm, y_cm) => setPickerFor({ bedId, x_cm, y_cm })}
                onPlantMove={handlePlantMove}
                onPlantUpdateLocal={handlePlantUpdateLocal}
                onPlantDelete={handlePlantDelete}
              />
            </div>

            {/* Summary */}
            <aside className="kaylas-card p-4 self-start" data-testid="builder-summary">
              <h3 className="font-display text-xl text-[var(--mum-sapphire)] mb-2">Summary</h3>
              {summary ? (
                <ul className="text-sm space-y-1">
                  <li><strong>{summary.total_beds}</strong> bed{summary.total_beds === 1 ? "" : "s"}</li>
                  <li><strong>{summary.total_plants}</strong> plant{summary.total_plants === 1 ? "" : "s"}</li>
                  <li><strong>{summary.bed_area_m2} m²</strong> in beds</li>
                  <li><strong>{summary.available_area_m2} m²</strong> available</li>
                </ul>
              ) : <p className="text-sm italic text-[var(--mum-cobalt)]/70">Loading…</p>}
              {summary && summary.by_variety.length > 0 && (
                <>
                  <h4 className="font-display text-sm uppercase tracking-wider text-[var(--mum-cobalt)] mt-4 mb-1">By variety</h4>
                  <ul className="text-sm space-y-0.5 max-h-48 overflow-y-auto">
                    {summary.by_variety.map((v) => (
                      <li key={v.name} className="flex justify-between border-b border-[var(--mum-sky)]/60 py-0.5"><span className="truncate pr-2">{v.name}</span><span className="tabular-nums text-[var(--mum-cobalt)]">{v.count}</span></li>
                    ))}
                  </ul>
                </>
              )}
            </aside>
          </div>
        </>
      )}

      {!active && (
        <div className="kaylas-card-quiet p-10 text-center">
          <Map size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">No gardens yet.</p>
          <button className="mum-btn mt-5" onClick={handleAddGarden}><Plus size={15} /> Create first garden</button>
        </div>
      )}

      {/* Plant picker drawer */}
      <KaylasDrawer open={!!pickerFor} onClose={() => setPickerFor(null)} title="Choose a plant" testid="builder-plant-picker">
        <PlantPicker onPick={handlePlantPlace} />
      </KaylasDrawer>

      {/* Edit garden drawer */}
      <KaylasDrawer open={!!editGarden} onClose={() => setEditGarden(null)} title="Edit garden" testid="builder-edit-garden-drawer">
        <GardenEditForm garden={editGarden} onSaved={async (fields) => {
          await kaylasApi.updateGarden(editGarden.id, fields);
          await refresh();
          setEditGarden(null);
          toast.success("Garden updated");
        }} onClose={() => setEditGarden(null)} />
      </KaylasDrawer>

      {/* Edit bed drawer */}
      <KaylasDrawer open={!!editBed} onClose={() => setEditBed(null)} title="Edit bed" testid="builder-edit-bed-drawer">
        <BedEditForm bed={editBed} onSaved={async (fields) => {
          await handleBedUpdate(editBed.id, fields);
          setEditBed(null);
          toast.success("Bed updated");
        }} onClose={() => setEditBed(null)} />
      </KaylasDrawer>
    </div>
  );
}

/* ────────────── Canvas ─────────────── */
function GardenCanvas({ garden, showMature, onBedUpdate, onBedDelete, onBedEdit, onAddPlantHere, onPlantMove, onPlantUpdateLocal, onPlantDelete }) {
  const W = cm2px(garden.width_cm);
  const H = cm2px(garden.length_cm);
  const svgRef = useRef(null);
  const dragRef = useRef(null); // { type: 'bed'|'plant', id, parentId, offsetX, offsetY }

  const getSvgPt = useCallback((evt) => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const cx = evt.clientX - rect.left;
    const cy = evt.clientY - rect.top;
    return { x_cm: cx / PX_PER_CM, y_cm: cy / PX_PER_CM, px_x: cx, px_y: cy };
  }, []);

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const pt = getSvgPt(e);
    const d = dragRef.current;
    if (d.type === "bed") {
      const bed = garden.beds.find((b) => b.id === d.id);
      if (!bed) return;
      const nx = Math.max(0, Math.min(garden.width_cm - bed.length_cm, pt.x_cm - d.offsetX));
      const ny = Math.max(0, Math.min(garden.length_cm - bed.width_cm, pt.y_cm - d.offsetY));
      onBedUpdate(bed.id, { x: nx, y: ny });
    } else if (d.type === "plant") {
      const bed = garden.beds.find((b) => b.id === d.parentId);
      if (!bed) return;
      const local_x = Math.max(0, Math.min(bed.length_cm, pt.x_cm - bed.x - d.offsetX));
      const local_y = Math.max(0, Math.min(bed.width_cm, pt.y_cm - bed.y - d.offsetY));
      onPlantUpdateLocal(bed.id, d.id, { x: local_x, y: local_y });
    }
  };

  const onPointerUp = async () => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    if (d.type === "plant") {
      const bed = garden.beds.find((b) => b.id === d.parentId);
      const plant = bed?.plants?.find((p) => p.id === d.id);
      if (plant) await onPlantMove(bed.id, plant.id, plant.x, plant.y);
    }
  };

  const onBedMouseDown = (e, bed) => {
    const pt = getSvgPt(e);
    dragRef.current = { type: "bed", id: bed.id, offsetX: pt.x_cm - bed.x, offsetY: pt.y_cm - bed.y };
    e.stopPropagation();
  };

  const onPlantMouseDown = (e, bed, plant) => {
    const pt = getSvgPt(e);
    dragRef.current = { type: "plant", id: plant.id, parentId: bed.id, offsetX: pt.x_cm - bed.x - plant.x, offsetY: pt.y_cm - bed.y - plant.y };
    e.stopPropagation();
  };

  const onBedDoubleClick = (e, bed) => {
    const pt = getSvgPt(e);
    const local_x = pt.x_cm - bed.x;
    const local_y = pt.y_cm - bed.y;
    if (local_x < 0 || local_y < 0 || local_x > bed.length_cm || local_y > bed.width_cm) return;
    onAddPlantHere(bed.id, local_x, local_y);
  };

  return (
    <svg
      ref={svgRef}
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      style={{ background: "linear-gradient(180deg, rgba(201,222,236,0.35), rgba(255,253,246,0.5))", borderRadius: 12, touchAction: "none", display: "block" }}
      data-testid="builder-canvas"
    >
      {/* Garden border */}
      <rect x={0} y={0} width={W} height={H} fill="transparent" stroke="var(--mum-sapphire)" strokeWidth="2" rx={10} />
      {/* Grid lines (1m grid) */}
      {Array.from({ length: Math.floor(garden.width_cm / 100) + 1 }).map((_, i) => (
        <line key={`gv${i}`} x1={cm2px(i * 100)} y1={0} x2={cm2px(i * 100)} y2={H} stroke="var(--mum-cornflower)" strokeWidth="0.5" opacity="0.25" />
      ))}
      {Array.from({ length: Math.floor(garden.length_cm / 100) + 1 }).map((_, i) => (
        <line key={`gh${i}`} x1={0} y1={cm2px(i * 100)} x2={W} y2={cm2px(i * 100)} stroke="var(--mum-cornflower)" strokeWidth="0.5" opacity="0.25" />
      ))}

      {/* Beds */}
      {(garden.beds || []).map((bed) => (
        <g key={bed.id} data-testid={`bed-${bed.id}`}>
          <rect
            x={cm2px(bed.x)} y={cm2px(bed.y)}
            width={cm2px(bed.length_cm)} height={cm2px(bed.width_cm)}
            fill="rgba(255,253,246,0.92)" stroke="var(--mum-lead)" strokeWidth="1.5" rx={4}
            onMouseDown={(e) => onBedMouseDown(e, bed)}
            onDoubleClick={(e) => onBedDoubleClick(e, bed)}
            style={{ cursor: "move" }}
          />
          {/* Bed label */}
          <text x={cm2px(bed.x) + 6} y={cm2px(bed.y) + 14} fill="var(--mum-sapphire)" fontFamily="Cormorant Garamond, Georgia, serif" fontSize="14">
            {bed.name} · {Math.round(bed.length_cm / 10) / 10}m × {Math.round(bed.width_cm / 10) / 10}m
          </text>
          {/* Bed action chips */}
          <g transform={`translate(${cm2px(bed.x) + cm2px(bed.length_cm) - 50},${cm2px(bed.y) + 4})`}>
            <foreignObject width="46" height="22">
              <div style={{ display: "flex", gap: 2 }}>
                <button onClick={(e) => { e.stopPropagation(); onBedEdit(bed); }} title="Edit bed" style={btnIcon} data-testid={`bed-edit-${bed.id}`}><Edit3 size={11} /></button>
                <button onClick={(e) => { e.stopPropagation(); onBedDelete(bed.id); }} title="Delete bed" style={btnIcon} data-testid={`bed-delete-${bed.id}`}><X size={12} /></button>
              </div>
            </foreignObject>
          </g>
          {/* Plants */}
          {(bed.plants || []).map((p) => {
            const cx = cm2px(bed.x + p.x);
            const cy = cm2px(bed.y + p.y);
            const spacingR = cm2px(p.spacing_cm / 2);
            const matureR = cm2px(p.mature_cm / 2);
            return (
              <g key={p.id} data-testid={`plant-${p.id}`}>
                {showMature && <circle cx={cx} cy={cy} r={matureR} fill="rgba(46,93,168,0.10)" stroke="var(--mum-cornflower)" strokeWidth="0.5" strokeDasharray="2 2" />}
                <circle cx={cx} cy={cy} r={spacingR} fill="rgba(110,155,201,0.18)" stroke="var(--mum-cornflower)" strokeWidth="0.7" />
                <circle
                  cx={cx} cy={cy} r={Math.max(6, spacingR * 0.32)}
                  fill={p.colour ? colourSwatch(p.colour) : "var(--mum-cobalt)"}
                  stroke="var(--mum-midnight)" strokeWidth="0.8"
                  onMouseDown={(e) => onPlantMouseDown(e, bed, p)}
                  onDoubleClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove ${p.name}?`)) onPlantDelete(bed.id, p.id); }}
                  style={{ cursor: "grab" }}
                />
                <text x={cx + 8} y={cy + 4} fontSize="10" fill="var(--mum-lead)" fontFamily="Inter, sans-serif" pointerEvents="none">{p.name}</text>
              </g>
            );
          })}
        </g>
      ))}

      {/* Hint */}
      <text x={10} y={H - 10} fontSize="10" fill="var(--mum-cobalt)" opacity="0.65" pointerEvents="none">
        Drag beds & plants · Double-tap a bed to add a plant · Double-tap a plant to remove
      </text>
    </svg>
  );
}

const btnIcon = {
  background: "var(--mum-ivory)",
  border: "1px solid var(--mum-cornflower)",
  color: "var(--mum-sapphire)",
  borderRadius: 999,
  width: 22, height: 22,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer",
};

function colourSwatch(label) {
  if (!label) return "var(--mum-cobalt)";
  const l = label.toLowerCase();
  if (l.includes("white") || l.includes("cream")) return "#FFF6E0";
  if (l.includes("pink")) return "#E9A4B6";
  if (l.includes("peach") || l.includes("coral") || l.includes("apricot")) return "#E6A077";
  if (l.includes("yellow") || l.includes("gold")) return "#E7C76B";
  if (l.includes("orange")) return "#D67843";
  if (l.includes("red") || l.includes("scarlet")) return "#B73B3B";
  if (l.includes("purple") || l.includes("violet")) return "#7A4FA6";
  if (l.includes("burgundy") || l.includes("dark")) return "#5A1F2B";
  if (l.includes("blue")) return "#2E5DA8";
  if (l.includes("green") || l.includes("lime")) return "#7B9C5A";
  return "var(--mum-cobalt)";
}

/* ────────────── Plant Picker ─────────────── */
function PlantPicker({ onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const d = await kaylasApi.database();
        if (!off) {
          // Only items that can be placed in the garden: tubers, blooms, flowers
          setItems((d.items || []).filter((i) => i.kind !== "seed"));
        }
      } finally { if (!off) setLoading(false); }
    })();
    return () => { off = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => (i.name || "").toLowerCase().includes(q));
  }, [items, search]);

  if (loading) return <div className="py-10 text-center"><Loader2 size={20} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>;
  if (items.length === 0) return <p className="text-sm font-display italic text-[var(--mum-cobalt)]/70">No tubers, blooms or flowers in your database yet — add some first.</p>;

  return (
    <div className="space-y-3" data-testid="plant-picker-list">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
        <input className="mum-field pl-9" placeholder="Search your garden…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="plant-picker-search" />
      </div>
      <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
        {filtered.map((i) => (
          <li key={`${i.kind}-${i.id}`}>
            <button
              type="button"
              onClick={() => onPick(i)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--mum-sky)]/40 text-left"
              data-testid={`pick-${i.id}`}
            >
              <div className="w-10 h-10 rounded-md overflow-hidden bg-[var(--mum-sky)]/50 flex items-center justify-center shrink-0">
                {i.photo ? <img src={i.photo} alt="" className="w-full h-full object-cover" /> : <span className="text-[var(--mum-cobalt)]/60 text-xs">—</span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-base text-[var(--mum-sapphire)] truncate">{i.name}</div>
                <div className="text-[0.65rem] uppercase tracking-wider text-[var(--mum-cobalt)]/70">{i.kind}{i.subtitle ? ` · ${i.subtitle}` : ""}</div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ────────────── Edit forms ─────────────── */
function GardenEditForm({ garden, onSaved, onClose }) {
  const [name, setName] = useState(garden?.name || "");
  const [w, setW] = useState(garden?.width_cm || 1000);
  const [l, setL] = useState(garden?.length_cm || 1000);
  if (!garden) return null;
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSaved({ name: name.trim() || garden.name, width_cm: Number(w) || garden.width_cm, length_cm: Number(l) || garden.length_cm }); }} className="space-y-4" data-testid="garden-edit-form">
      <FormSection title="Garden">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Name</span>
          <input className="mum-field mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="garden-edit-name" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Width (cm)</span>
            <input type="number" min="100" className="mum-field mt-1.5" value={w} onChange={(e) => setW(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Length (cm)</span>
            <input type="number" min="100" className="mum-field mt-1.5" value={l} onChange={(e) => setL(e.target.value)} />
          </label>
        </div>
        <p className="text-xs text-[var(--mum-cobalt)]/70">100 cm = 1 metre. Try 1000 × 1000 for a 10 m × 10 m patch.</p>
      </FormSection>
      <div className="flex gap-2">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1">Save</button>
      </div>
    </form>
  );
}

function BedEditForm({ bed, onSaved, onClose }) {
  const [name, setName] = useState(bed?.name || "");
  const [l, setL] = useState(bed?.length_cm || 200);
  const [w, setW] = useState(bed?.width_cm || 100);
  if (!bed) return null;
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSaved({ name: name.trim() || bed.name, length_cm: Number(l) || bed.length_cm, width_cm: Number(w) || bed.width_cm }); }} className="space-y-4" data-testid="bed-edit-form">
      <FormSection title="Bed">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Name</span>
          <input className="mum-field mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="bed-edit-name" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Length (cm)</span>
            <input type="number" min="20" className="mum-field mt-1.5" value={l} onChange={(e) => setL(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Width (cm)</span>
            <input type="number" min="20" className="mum-field mt-1.5" value={w} onChange={(e) => setW(e.target.value)} />
          </label>
        </div>
      </FormSection>
      <div className="flex gap-2">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1">Save</button>
      </div>
    </form>
  );
}
